import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Divider,
} from '@mui/material';

export const PARENT_CONSENT_VERSION = '2026-10-06';

const READ_SECTIONS = [
  {
    title: '1. Nature of the Program',
    paragraphs: [
      'I understand that Bacchapanti offers online educational and developmental activities intended to encourage learning, creativity, concentration, confidence, observation, participation, and overall child development through fun interactive sessions.',
      'I understand that these activities are educational and recreational in nature and are not medical, psychological, therapeutic, diagnostic, or scientifically validated treatments.',
    ],
  },
  {
    title: '2. Blindfold and Sensory-Based Activities',
    paragraphs: [
      'I understand that these sessions may include blindfold-based, sensory, mindfulness, observation, or other interactive activities designed for educational, recreational, and engagement purposes.',
      'I understand that such activities are conducted solely as part of the educational program. Bacchapanti does not claim that these activities provide supernatural, medical, therapeutic, scientifically established, or extraordinary abilities, nor does it guarantee any particular outcome.',
      'I voluntarily consent to my child\'s participation in such activities.',
    ],
  },
  {
    title: '3. Parent/Guardian Supervision, Technology and Activity Materials',
    paragraphs: [
      'I understand that the program is conducted online and that I am responsible for providing my child with an appropriate environment, suitable device, internet connection, and reasonable parental/guardian supervision during the sessions.',
      'I also understand that certain educational, developmental, sensory, or recreational activities may require basic materials or items to be used by the child during or before the session, such as cards, paper, pens, pencils, notebooks, household items, activity materials, or other items communicated by Bacchapanti.',
      'I acknowledge that arranging and making available such required or recommended materials for my child\'s participation is the responsibility of the parent/guardian, unless Bacchapanti specifically states that a particular material or kit will be provided by Bacchapanti.',
      'I agree to inform Bacchapanti\'s team of any relevant concern, limitation, sensitivity, or circumstance that may affect my child\'s safe and comfortable participation.',
    ],
  },
  {
    title: '4. Online Participation',
    paragraphs: [
      'I understand that sessions may be conducted through platforms such as Zoom, Google Meet, or other digital platforms.',
      'I acknowledge that the quality and continuity of the online session may depend on internet connectivity, device availability, and the third-party platform being used.',
    ],
  },
  {
    title: '5. Personal Information',
    paragraphs: [
      'I consent to Bacchapanti collecting, storing, and processing the personal information provided during registration, including my name, child\'s name, age, contact details and other information reasonably required for enrollment, communication, attendance, customer support, and related program activities.',
      'Bacchapanti will take reasonable steps to protect this information and will not disclose it to third parties except where required by law or where reasonably necessary to provide or administer the program.',
    ],
  },
  {
    title: '6. Communication Consent',
    paragraphs: [
      'I agree to receive program-related communications, including session links, reminders, attendance-related communication, certificates, updates, discounts/offers and other information through WhatsApp, phone calls, SMS, or email.',
    ],
  },
  {
    title: '7. No Guarantee of Specific Results',
    paragraphs: [
      'I understand that every child learns and develops differently. Bacchapanti does not guarantee any specific educational, developmental, academic, cognitive, behavioural, or other outcome from participation in the program.',
    ],
  },
  {
    title: '8. Informed Consent and Acknowledgment of Possible Discomfort',
    paragraphs: [
      'I understand that participation in interactive, mindfulness, sensory, or concentration-based activities may occasionally result in temporary or mild discomfort, tiredness or sleepiness, particularly in some children or neurodivergent learners.',
      'I have considered my child\'s individual needs and circumstances and believe that my child is reasonably able to participate in the program.',
      'I understand that I may stop or withdraw my child\'s participation from any activity if I believe that the activity is causing significant discomfort or is not appropriate for my child.',
      'To the extent permitted by applicable law, I agree that Bacchapanti, its owners, employees, trainers, instructors, and representatives will not be responsible for risks or consequences arising from my child\'s voluntary participation, except to the extent caused by their negligence, willful misconduct, or any liability that cannot legally be excluded or limited',
    ],
  },
];

const MEDIA_CONSENT_YES = 'I consent the use of my child\'s session media—including screenshots, video clips, and testimonials—strictly for non-commercial educational and informational uses.';
const MEDIA_CONSENT_NO = 'I do not consent the use of my child\'s session media—including screenshots, video clips, and testimonials—strictly for non-commercial educational and informational uses.';
const AGREEMENT_LABEL = 'I have read, understood, and agreed to the above Parent Consent & Declaration and consent to my child\'s participation in the Bacchapanti Online Kids Development Program.';

function formatToday() {
  return new Date().toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function ChoiceOption({ name, value, selected, label, onSelect }) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.75,
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        cursor: 'pointer',
        minHeight: 56,
      }}
    >
      <Checkbox
        name={name}
        checked={selected}
        onChange={() => onSelect(selected ? '' : value)}
        inputProps={{ 'aria-label': label }}
        sx={{ p: 0.25, mt: 0.15 }}
      />
      <Typography sx={{ fontSize: '1rem', lineHeight: 1.45, pt: 0.35 }}>{label}</Typography>
    </Box>
  );
}

function ChoiceGroup({ title, purpose, name, value, error, options, onSelect }) {
  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography component="h3" sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '1rem', lineHeight: 1.65, mb: 1.25 }}>
        {purpose}
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.25 }}>
        {options.map((option) => (
          <ChoiceOption
            key={option.value}
            name={name}
            value={option.value}
            selected={value === option.value}
            label={option.label}
            onSelect={onSelect}
          />
        ))}
      </Box>
      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function ParentConsentSection({
  sectionRef,
  signerName,
  childName,
  mediaUse,
  agreed,
  signature,
  errors,
  onChange,
}) {
  const [reachedEnd, setReachedEnd] = useState(false);
  const displaySigner = signerName?.trim() || 'the parent or guardian named above';
  const displayChild = childName?.trim() || 'your child';

  const handleScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 28) {
      setReachedEnd(true);
    }
  };

  return (
    <Box ref={sectionRef} sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
        Bacchapanti Online Program – Parent Consent & Declaration
      </Typography>

      <Box
        tabIndex={0}
        onScroll={handleScroll}
        aria-label="Parent consent and declaration"
        sx={{
          mt: 2,
          maxHeight: { xs: 340, sm: 380 },
          overflowY: 'auto',
          px: { xs: 1.75, sm: 2.5 },
          py: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          fontSize: '1rem',
          lineHeight: 1.65,
        }}
      >
        <Typography sx={{ fontSize: '1rem', lineHeight: 1.65, mb: 2 }}>
          By submitting this registration form, I, as the parent/legal guardian of the child, confirm that I have read, understood, and voluntarily agree to the following:
        </Typography>
        {READ_SECTIONS.map((section) => (
          <Box key={section.title} sx={{ mb: 2.25 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.75 }}>
              {section.title}
            </Typography>
            {section.paragraphs.map((paragraph) => (
              <Typography key={paragraph} sx={{ fontSize: '1rem', lineHeight: 1.65, mb: 1 }}>
                {paragraph}
              </Typography>
            ))}
          </Box>
        ))}
      </Box>
      <Typography variant="body2" color={reachedEnd ? 'success.main' : 'text.secondary'} sx={{ mt: 1 }}>
        {reachedEnd ? 'You have reached the end of the consent.' : 'Please scroll to the end of the consent.'}
      </Typography>

      <ChoiceGroup
        title="9. Screenshots & Videos"
        purpose="From time to time screenshots & short activity videos during the session; testimonials, or success stories may be collected or used for quality assurance, trainer review, security, child analysis & progress, educational or informational purposes."
        name="media_use_consent"
        value={mediaUse}
        error={errors.media_use_consent}
        onSelect={(value) => onChange('mediaUse', value)}
        options={[
          { value: 'yes', label: MEDIA_CONSENT_YES },
          { value: 'no', label: MEDIA_CONSENT_NO },
        ]}
      />

      <Box
        sx={{
          mt: 3,
          p: { xs: 1.75, sm: 2.25 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: errors.agreement_accepted || errors.electronic_signature ? 'error.main' : 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.75 }}>
          10. Final Declaration, Consent and Electronic Signature
        </Typography>
        <Typography sx={{ fontSize: '1rem', lineHeight: 1.65, mb: 2 }}>
          I confirm that the information provided by me is true and accurate. I confirm that I have read and understood this Parent Consent & Declaration, including the nature of the program, possible risks and discomforts, online participation requirements, parent/guardian responsibilities, recording and media-consent options, and the limitations regarding program outcomes.
        </Typography>
        <Typography sx={{ fontSize: '1rem', lineHeight: 1.55 }}>
          Signing as <strong>{displaySigner}</strong> for <strong>{displayChild}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Date: {formatToday()}
        </Typography>

        <Box
          component="label"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mt: 2,
            cursor: 'pointer',
          }}
        >
          <Checkbox
            checked={agreed}
            onChange={(event) => onChange('agreed', event.target.checked)}
            sx={{ p: 0.25, mt: 0.15 }}
            inputProps={{ 'aria-label': AGREEMENT_LABEL }}
          />
          <Typography sx={{ fontSize: '1rem', lineHeight: 1.5, pt: 0.35 }}>
            {AGREEMENT_LABEL}
          </Typography>
        </Box>
        {errors.agreement_accepted ? (
          <Typography variant="body2" color="error" sx={{ mt: 1, ml: 4.5 }}>
            {errors.agreement_accepted}
          </Typography>
        ) : null}

        <TextField
          fullWidth
          label="Electronic Signature"
          value={signature}
          onChange={(event) => onChange('signature', event.target.value)}
          error={!!errors.electronic_signature}
          helperText={errors.electronic_signature || 'Typing my full name above constitutes my electronic signature and confirmation of this consent.'}
          variant="outlined"
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 200, autoComplete: 'name' }}
        />
      </Box>
    </Box>
  );
}
